import { useEffect, useState } from 'react';

/**
 * An <img> that opens a full-screen, uncropped lightbox when clicked, so
 * reviewers can inspect images at full size — defects that hide in small
 * previews become visible. `className` styles the INLINE image only; the
 * lightbox always shows the full image (object-contain, max 90vw/90vh).
 *
 * Layout note: this renders a bare <img> (not a wrapper) so call-site layout
 * (w-full, aspect ratios, object-cover) is preserved exactly — we only add
 * cursor-zoom-in + click/keyboard handlers.
 */
export function ZoomableImage({
  src,
  alt = '',
  className,
}: {
  src: string;
  alt?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  // Mirror the Drawer pattern (components/ui/drawer.tsx): close on Escape and
  // lock body scroll while the lightbox is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Use capture phase (registered below with `true`) + stopPropagation so
        // that closing the lightbox does NOT also close a parent Drawer/editor
        // that listens for Escape on window in the bubble phase. Capture fires
        // first; stopping propagation here prevents bubble-phase listeners from
        // ever seeing the event.
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <img
        src={src}
        alt={alt}
        className={`${className ?? ''} cursor-zoom-in`.trim()}
        role="button"
        tabIndex={0}
        aria-label="Kép megnyitása teljes méretben"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Bezárás"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-2xl leading-none text-white hover:bg-black/70"
          >
            ✕
          </button>
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
        </div>
      )}
    </>
  );
}
